import { Injectable, signal } from '@angular/core';
import { Capacitor } from '@capacitor/core';

type NetState = {
  online: boolean;
  type?: string;
};

@Injectable({ providedIn: 'root' })
export class NetworkService {
  readonly state = signal<NetState>({ online: true, type: 'init' });

  private capListener?: { remove: () => Promise<void> };
  private pollTimer: any = null;
  private NetworkCap?: typeof import('@capacitor/network').Network;

  isOnline(): boolean {
    return this.state().online;
  }

  async init(): Promise<void> {
    const isNative = Capacitor.isNativePlatform();

    // ===== WEB =====
    if (!isNative) {
      this.setState(navigator.onLine, 'web:init');
      window.addEventListener('online',  () => this.setState(true,  'web'));
      window.addEventListener('offline', () => this.setState(false, 'web'));
      return;
    }

    // ===== NATIVE =====
    const { Network } = await import('@capacitor/network');
    this.NetworkCap = Network;

    // Estado inicial REAL
    await this.refresh('native:init');

    // Listener real de red
    this.capListener = await Network.addListener('networkStatusChange', (s) => {
      this.setState(!!s.connected, `native:${s.connectionType ?? 'unknown'}`);
    });

    // Fallback: polling (iOS simulator suele ser traicionero)
    // Si queda offline, pollea hasta recuperar
    if (!this.isOnline()) this.startPolling();
  }

  async refresh(tag = 'native:refresh'): Promise<void> {
    const isNative = Capacitor.isNativePlatform();

    if (!isNative) {
      this.setState(navigator.onLine, 'web:refresh');
      return;
    }

    if (!this.NetworkCap) {
      const { Network } = await import('@capacitor/network');
      this.NetworkCap = Network;
    }

    try {
      const s = await this.NetworkCap.getStatus();
      this.setState(!!s.connected, `${tag}:${s.connectionType ?? 'unknown'}`);
    } catch {
      // no romper
    }
  }

  private setState(online: boolean, type?: string) {
    this.state.set({ online, type });

    // si estoy offline, polling hasta online
    if (!online) this.startPolling();
    else this.stopPolling();
  }

  private startPolling() {
    if (this.pollTimer) return;
    if (!Capacitor.isNativePlatform()) return;
    if (!this.NetworkCap) return;

    this.pollTimer = setInterval(async () => {
      try {
        const s = await this.NetworkCap!.getStatus();
        if (s.connected) this.setState(true, `native:poll:${s.connectionType ?? 'unknown'}`);
      } catch {}
    }, 1200);
  }

  private stopPolling() {
    if (!this.pollTimer) return;
    clearInterval(this.pollTimer);
    this.pollTimer = null;
  }

  async destroy(): Promise<void> {
    this.stopPolling();
    try { await this.capListener?.remove(); } catch {}
  }
}